import { useMediaQuery } from '@mui/material';
import { Theme } from '@mui/system';
import { useGetIdentity, usePermissions } from 'react-admin';
import { AppPermissions } from '../auth-provider';
import { AccountRole } from '@football-kids/shared/enums';
import get from 'lodash.get';
import RouterLink from '../components/common/RouterLink';

export const useIsInAppBrowser = () => {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any)?.opera;
    return (userAgent.indexOf("FBAN") > -1) || (userAgent.indexOf("FBAV") > -1);
}

export const useIsMobile = () => {
    return !useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
};

export const useIsPWA = () => {
    return (
        ((navigator as any)?.standalone as boolean) ||
        window.matchMedia('(display-mode: standalone)').matches
    );
};

export const useIsIOS = () => {
    return (
        [
            'iPad Simulator',
            'iPhone Simulator',
            'iPod Simulator',
            'iPad',
            'iPhone',
            'iPod',
        ].includes(navigator.platform) ||
        // iPad on iOS 13 detection
        (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    );
};

export const useIsMac = () => {
    return (
        navigator.userAgent.includes('Mac')
    );
};

export const useMultiFilter = <IdType = string>(
    filterKey: string,
    valueKey = 'id'
) => {
    const isSelectedFn = (value: Record<string, any>, filters: any) => {
        const regions = filters[filterKey] || [];
        return regions.includes(value[valueKey]);
    };

    const toggleFilter = (value: Record<string, any>, filters: any) => {
        const items: (number | string)[] = filters[filterKey] || [];
        return {
            ...filters,
            [filterKey]: items.includes(value[valueKey])
                ? items.filter((v) => v !== value[valueKey])
                : [...items, value[valueKey]],
        };
    };

    return [isSelectedFn, toggleFilter];
};

export const useSchoolsIdsFilter = () => {
    const { permissions } = usePermissions<AppPermissions>();
    const { data: identityData } = useGetIdentity();
    const schoolsIds = identityData?.account?.schoolsIds || [];

    if (permissions[AccountRole.MANAGER]) {
        return {
            ids: schoolsIds,
        };
    }

    return {};
};

export const useSportsWearStorageCount = () => {
    const { data: identityData } = useGetIdentity();
    return identityData?.account?.sportsWearStorage;
};


export const getEnv = (name: string, typ?: "boolean" | "int" ) => {
    // @ts-ignore
    const value = import.meta.env[name]

    if (!value) {
        return null
    }

    if (typ === 'boolean') {
        return JSON.parse(value);
    }

    if (typ === 'int') {
        return Number(value);
    }

    return value

}


export const useTabRouting = (basePath: string, tabs: string[], options?: {
    defaultTab?: string
}) => {
    const defaultTab = get(options, 'defaultTab', null) ?
        basePath + '/' + options!.defaultTab
        : null;

    const currentTab = tabs
        .map(x => `${basePath}/${x}`)
        .find(x => location.pathname.startsWith(x)) || defaultTab;

    const createTabProps = (tab: string) => ({
        component: RouterLink,
        to: basePath + '/' + tab,
        value: basePath + '/' + tab
    });

    return [currentTab, createTabProps] as const;
};
